import { navigate } from "../lib/router";
import { Button } from "./ui/button";
import logoIcon from "../assets/icone.png";
import { useLanguage } from "../hooks/useLanguage";
import { Globe, Menu, X } from "lucide-react";
import { useState } from "react";

export interface HeaderLink {
  label: string;
  onClick?: () => void;
  href?: string;
}

interface HeaderProps {
  navigationLinks?: HeaderLink[];
  showAuthButtons?: boolean;
  onLoginClick?: () => void;
  onRegisterClick?: () => void;
}

export function Header({
  navigationLinks,
  showAuthButtons = true,
  onLoginClick = () => navigate("/login"),
  onRegisterClick = () => navigate("/register")
}: HeaderProps) {
  const { language, setLanguage, t } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Default links if not provided, using translations
  const links = navigationLinks || [
    { label: t.header.home, href: "/" },
    { label: t.header.qrCode, href: "/qr" },
    { label: t.header.documentGenerator, href: "/generate-cpf-cnpj" },
    { label: t.header.documentValidator, href: "/validate-cpf-cnpj" }
  ];

  const handleLinkClick = (link: HeaderLink) => {
    setIsMobileMenuOpen(false); // Close mobile menu when link is clicked
    if (link.onClick) {
      link.onClick();
    } else if (link.href) {
      if (link.href.startsWith('http')) {
        window.open(link.href, '_blank');
      } else {
        navigate(link.href);
      }
    }
  };

  const toggleLanguage = () => {
    setLanguage(language === 'pt' ? 'en' : 'pt');
  };

  const handleAuthClick = (callback: () => void) => {
    setIsMobileMenuOpen(false);
    callback();
  };

  return (
    <header className="max-w-7xl mx-auto px-6 pt-4 pb-12 md:pt-8 md:pb-16">
      {/* Navigation */}
      <nav className="flex justify-between items-center mb-8 md:mb-12" aria-label="Navegação principal">
        <div
          className="flex items-center gap-2 font-bold text-xl tracking-tight text-slate-900 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate("/")}
        >
          <img src={logoIcon} alt="Hub Links Logo" className="w-8 h-8 rounded-lg" />
          Hub Links
        </div>

        {/* Desktop Navigation Links */}
        <div className="hidden md:flex gap-6 text-sm font-medium text-slate-600">
          {links.map((link, index) => (
            <Button
              key={index}
              variant="link"
              className="hover:text-brand-primary transition-colors"
              onClick={() => handleLinkClick(link)}
            >
              {link.label}
            </Button>
          ))}
        </div>

        {/* Desktop Auth Buttons and Language Selector */}
        <div className="hidden md:flex items-center gap-3">
          {/* Language Selector */}
          <Button
            variant="ghost"
            size="micro"
            className="text-slate-600 hover:text-brand-primary"
            onClick={toggleLanguage}
            aria-label={t.header.switchLanguage}
          >
            <Globe className="w-4 h-4 mr-2" />
            {language === 'pt' ? 'PT' : 'EN'}
          </Button>

          {showAuthButtons && (
            <>
              <Button
                variant="ghost"
                className="text-slate-600 hover:text-brand-primary hover:bg-brand-primary/10"
                onClick={onLoginClick}
              >
                {t.header.login}
              </Button>
              <Button variant="primary" onClick={onRegisterClick}>
                {t.header.signup}
              </Button>
            </>
          )}
        </div>

        {/* Mobile Hamburger Menu Button */}
        <button
          className="md:hidden p-2 text-slate-600 hover:text-brand-primary transition-colors"
          onClick={() => setIsMobileMenuOpen(true)}
          aria-label={t.header.openMenu}
        >
          <Menu className="w-6 h-6" />
        </button>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Mobile Menu Drawer */}
          <div className="fixed top-0 right-0 bottom-0 w-80 max-w-[85vw] bg-white shadow-2xl z-50 md:hidden overflow-y-auto">
            {/* Menu Header */}
            <div className="flex justify-between items-center p-6 border-b border-slate-200">
              <div className="flex items-center gap-2 font-bold text-xl tracking-tight text-slate-900">
                <img src={logoIcon} alt="Hub Links Logo" className="w-8 h-8 rounded-lg" />
                Hub Links
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 text-slate-600 hover:text-brand-primary transition-colors"
                aria-label={t.header.closeMenu}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Menu Content */}
            <div className="p-6 space-y-6">
              {/* Navigation Links */}
              <div className="space-y-2">
                {links.map((link, index) => (
                  <button
                    key={index}
                    className="w-full text-left px-4 py-3 text-slate-700 hover:bg-brand-primary/10 hover:text-brand-primary rounded-lg transition-colors font-medium"
                    onClick={() => handleLinkClick(link)}
                  >
                    {link.label}
                  </button>
                ))}
              </div>

              {/* Language Selector */}
              <div className="pt-4 border-t border-slate-200">
                <Button
                  variant="ghost"
                  className="w-full justify-start text-slate-600 hover:text-brand-primary"
                  onClick={toggleLanguage}
                  aria-label={t.header.switchLanguage}
                >
                  <Globe className="w-4 h-4 mr-2" />
                  {language === 'pt' ? 'Português' : 'English'}
                </Button>
              </div>

              {/* Auth Buttons */}
              {showAuthButtons && (
                <div className="pt-4 border-t border-slate-200 space-y-3">
                  <Button
                    variant="ghost"
                    className="w-full text-slate-600 hover:text-brand-primary hover:bg-brand-primary/10"
                    onClick={() => handleAuthClick(onLoginClick)}
                  >
                    {t.header.login}
                  </Button>
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={() => handleAuthClick(onRegisterClick)}
                  >
                    {t.header.signup}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </header>
  );
}
